'use client';

import { useEffect, useState, type FormEvent } from 'react';
import type { FormBlockData, FormFieldData } from './schema';
import { sanitizedHtmlProps } from '../lib/sanitize';

interface PublicFormConfig {
  key: string;
  title?: string;
  description?: string;
  submit_label: string;
  fields: FormFieldData[];
  success_message?: string | null;
  redirect_url?: string | null;
  honeypot_enabled: boolean;
  recaptcha_enabled: boolean;
  recaptcha_site_key?: string | null;
}

function resolveApiUrl(): string {
  const g = globalThis as {
    process?: { env?: Record<string, string | undefined> };
    __SYTEL_API_URL__?: string;
  };
  return (
    g.__SYTEL_API_URL__ ||
    g.process?.env?.NEXT_PUBLIC_API_URL ||
    'http://localhost:8001'
  );
}

const WIDTH_CLASS: Record<NonNullable<FormFieldData['width']>, string> = {
  full: 'v5-form-col-12',
  'two-thirds': 'v5-form-col-8',
  half: 'v5-form-col-6',
  third: 'v5-form-col-4',
};

export function FormRenderer({
  eyebrow,
  title,
  subtitle,
  formKey,
  submitLabel,
  successMessage,
  consent,
  fields,
  layout = 'centered',
  variant = 'default',
}: FormBlockData) {
  // If `fields` is provided inline, use those; otherwise fetch from API
  const [config, setConfig] = useState<PublicFormConfig | null>(() =>
    Array.isArray(fields) && fields.length > 0
      ? {
          key: formKey,
          title,
          submit_label: submitLabel ?? 'Enviar',
          fields,
          success_message: successMessage,
          redirect_url: null,
          honeypot_enabled: true,
          recaptcha_enabled: false,
          recaptcha_site_key: null,
        }
      : null,
  );
  const [loading, setLoading] = useState<boolean>(!config);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (config) return;
    let alive = true;
    setLoading(true);
    fetch(`${resolveApiUrl()}/api/v1/public/forms/${encodeURIComponent(formKey)}`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((json) => {
        if (!alive) return;
        const data = (json?.data ?? json) as PublicFormConfig;
        setConfig(data);
      })
      .catch((err) => {
        console.error('[FormBlock] could not load form config:', err);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [config, formKey]);

  // Load reCAPTCHA script if enabled
  useEffect(() => {
    if (!config?.recaptcha_enabled || !config.recaptcha_site_key) return;
    if (document.getElementById('recaptcha-v3')) return;
    const script = document.createElement('script');
    script.id = 'recaptcha-v3';
    script.src = `https://www.google.com/recaptcha/api.js?render=${config.recaptcha_site_key}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [config?.recaptcha_enabled, config?.recaptcha_site_key]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!config) return;
    setStatus('submitting');
    setErrorMsg(null);
    const fd = new FormData(e.currentTarget);
    const payload: Record<string, unknown> = {};
    fd.forEach((v, k) => {
      if (payload[k] === undefined) {
        payload[k] = v instanceof File ? v.name : String(v);
      } else {
        // Multiple values (checkboxes etc.)
        const prev = payload[k];
        payload[k] = Array.isArray(prev) ? [...prev, String(v)] : [String(prev), String(v)];
      }
    });

    // Honeypot trap
    if (config.honeypot_enabled && payload['_hp_field']) {
      setStatus('success'); // pretend success
      return;
    }
    delete payload['_hp_field'];

    // reCAPTCHA v3
    if (config.recaptcha_enabled && config.recaptcha_site_key) {
      try {
        const g = window as unknown as {
          grecaptcha?: { execute: (k: string, o: { action: string }) => Promise<string>; ready: (cb: () => void) => void };
        };
        if (g.grecaptcha) {
          const token = await new Promise<string>((resolve) => {
            g.grecaptcha!.ready(() => {
              g.grecaptcha!
                .execute(config.recaptcha_site_key!, { action: 'submit' })
                .then(resolve);
            });
          });
          payload['_recaptcha_token'] = token;
        }
      } catch (err) {
        console.warn('[FormBlock] recaptcha failed:', err);
      }
    }

    try {
      const res = await fetch(
        `${resolveApiUrl()}/api/v1/public/forms/${encodeURIComponent(formKey)}/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ data: payload }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus('success');
      (e.target as HTMLFormElement).reset();
      if (config.redirect_url) {
        setTimeout(() => {
          window.location.href = config.redirect_url!;
        }, 800);
      }
    } catch (err) {
      console.error('[FormBlock] submit failed:', err);
      setStatus('error');
      setErrorMsg('No se pudo enviar el formulario. Inténtalo de nuevo.');
    }
  }

  const sectionClass = `v5-section v5-form-section v5-form-layout-${layout} v5-form-variant-${variant}`;

  if (loading) {
    return (
      <section className={sectionClass} id={`formulario-${formKey}`}>
        <div className="container">
          <div className="v5-form-wrap">
            {(eyebrow || title || subtitle) && (
              <div className="v5-form-head">
                {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
                {title ? <h2 className="v5-form-h2">{title}</h2> : null}
                {subtitle ? <p className="v5-form-sub">{subtitle}</p> : null}
              </div>
            )}
            <p className="v5-form-loading">Cargando formulario…</p>
          </div>
        </div>
      </section>
    );
  }

  if (!config) {
    return (
      <section className={sectionClass} id={`formulario-${formKey}`}>
        <div className="container">
          <div className="v5-form-wrap v5-form-error-state">
            No se encontró el formulario <code>{formKey}</code>.
          </div>
        </div>
      </section>
    );
  }

  const buttonLabel = submitLabel ?? config.submit_label ?? 'Enviar';
  const okMessage = successMessage ?? config.success_message ?? 'Gracias. Te contactamos pronto.';
  const safeFields = config.fields ?? [];

  return (
    <section className={sectionClass} id={`formulario-${formKey}`}>
      <div className="container">
        <div className="v5-form-wrap">
          {(eyebrow || title || subtitle) && (
            <div className="v5-form-head">
              {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
              {title ? <h2 className="v5-form-h2">{title}</h2> : null}
              {subtitle ? <p className="v5-form-sub">{subtitle}</p> : null}
            </div>
          )}
          {status === 'success' ? (
            <div className="v5-form-success" role="status">{okMessage}</div>
          ) : (
            <form className="v5-form-grid" onSubmit={handleSubmit} noValidate>
              {safeFields.map((f) => (
                <FieldRenderer key={f.name} field={f} />
              ))}
              {config.honeypot_enabled && (
                <input
                  type="text"
                  name="_hp_field"
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
                />
              )}
              {consent ? (
                <label className="v5-form-consent v5-form-col-12">
                  <input type="checkbox" name="_consent" required />
                  <span dangerouslySetInnerHTML={sanitizedHtmlProps(consent)} />
                </label>
              ) : null}
              <div className="v5-form-col-12 v5-form-actions">
                <button
                  type="submit"
                  className="v5-form-submit"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Enviando…' : buttonLabel}
                </button>
                {errorMsg ? <span className="v5-form-error" role="alert">{errorMsg}</span> : null}
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function FieldRenderer({ field: f }: { field: FormFieldData }) {
  const widthClass = WIDTH_CLASS[f.width ?? 'full'];

  if (f.type === 'hidden') {
    return <input type="hidden" name={f.name} defaultValue={String(f.default ?? '')} />;
  }

  if (f.type === 'checkbox') {
    return (
      <label className={`v5-form-field v5-form-checkbox ${widthClass}`}>
        <input type="checkbox" name={f.name} required={f.required} value="1" />
        <span>
          {f.label}
          {f.required ? ' *' : ''}
        </span>
        {f.helper ? <small className="v5-form-help">{f.helper}</small> : null}
      </label>
    );
  }

  if (f.type === 'radio') {
    const opts = normalizeOptions(f.options);
    return (
      <fieldset className={`v5-form-field v5-form-radiogroup ${widthClass}`}>
        <legend className="v5-form-lbl">
          {f.label}
          {f.required ? ' *' : ''}
        </legend>
        {opts.map((o) => (
          <label key={o.value} className="v5-form-radio">
            <input type="radio" name={f.name} value={o.value} required={f.required} />
            <span>{o.label}</span>
          </label>
        ))}
        {f.helper ? <small className="v5-form-help">{f.helper}</small> : null}
      </fieldset>
    );
  }

  return (
    <label className={`v5-form-field ${widthClass}`}>
      <span className="v5-form-lbl">
        {f.label}
        {f.required ? ' *' : ''}
      </span>
      {f.type === 'textarea' ? (
        <textarea
          name={f.name}
          rows={4}
          required={f.required}
          placeholder={f.placeholder ?? ''}
          defaultValue={String(f.default ?? '')}
          minLength={f.validation?.min}
          maxLength={f.validation?.max}
        />
      ) : f.type === 'select' ? (
        <select name={f.name} required={f.required} defaultValue={String(f.default ?? '')}>
          {!f.default && (
            <option value="" disabled>
              {f.placeholder ?? '—'}
            </option>
          )}
          {normalizeOptions(f.options).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={mapInputType(f.type)}
          name={f.name}
          required={f.required}
          placeholder={f.placeholder ?? ''}
          defaultValue={String(f.default ?? '')}
          min={f.validation?.min}
          max={f.validation?.max}
          pattern={f.validation?.pattern}
        />
      )}
      {f.helper ? <small className="v5-form-help">{f.helper}</small> : null}
    </label>
  );
}

function mapInputType(t: FormFieldData['type']): string {
  if (t === 'phone') return 'tel';
  return t;
}

function normalizeOptions(opts?: FormFieldData['options']): Array<{ value: string; label: string }> {
  if (!opts) return [];
  if (opts.length === 0) return [];
  if (typeof opts[0] === 'string') {
    return (opts as string[]).map((s) => ({ value: s, label: s }));
  }
  return opts as Array<{ value: string; label: string }>;
}
